/**
 * Browser client for Stockfish 19 (lite, single-threaded WASM) running in a Web Worker.
 * Jobs are serialised: each analyse() call waits for the previous one, and an
 * AbortSignal sends "stop" so a newer request (e.g. the analysis board moving on)
 * takes over quickly.
 */
import { parseBestMove, parseInfo, toWhitePov, type Score } from "./uci";

const ENGINE_URL = "/engine/stockfish-19-lite-single.js";

export type EngineLine = {
  multipv: number;
  depth: number;
  /** White's point of view. */
  score: Score;
  pv: string[];
};

export type AnalyseOptions = {
  depth?: number;
  movetime?: number;
  infinite?: boolean;
  multipv?: number;
  /** Enables UCI_LimitStrength with this Elo (Stockfish accepts 1320..3190). */
  elo?: number;
  /** Stockfish "Skill Level", 0..20. */
  skill?: number;
  onUpdate?: (lines: EngineLine[], depth: number) => void;
  signal?: AbortSignal;
};

export type AnalyseResult = { bestmove: string | null; lines: EngineLine[]; depth: number };

export class StockfishEngine {
  private worker: Worker;
  private listeners = new Set<(line: string) => void>();
  private waiters = new Set<(err: Error) => void>();
  private ready: Promise<void>;
  private chain: Promise<unknown> = Promise.resolve();
  private dead = false;

  constructor(url = ENGINE_URL) {
    this.worker = new Worker(url);
    this.worker.onmessage = (e: MessageEvent) => {
      const text = typeof e.data === "string" ? e.data : String(e.data);
      for (const line of text.split("\n")) {
        for (const l of this.listeners) l(line.trim());
      }
    };
    // A failed download or a WASM crash ends this engine; getEngine() then starts a new one.
    this.worker.onerror = () => this.terminate(new Error("The engine failed to load"));
    this.ready = this.handshake();
    this.ready.catch((err: Error) => this.terminate(err));
  }

  get alive() {
    return !this.dead;
  }

  private send(cmd: string) {
    if (!this.dead) this.worker.postMessage(cmd);
  }

  private waitFor(pred: (line: string) => boolean, timeoutMs = 30000): Promise<string> {
    return new Promise((resolve, reject) => {
      const settle = () => {
        clearTimeout(timer);
        this.listeners.delete(fn);
        this.waiters.delete(fail);
      };
      const fail = (err: Error) => {
        settle();
        reject(err);
      };
      const fn = (line: string) => {
        if (pred(line)) {
          settle();
          resolve(line);
        }
      };
      const timer = setTimeout(() => fail(new Error("Engine timed out")), timeoutMs);
      if (this.dead) return fail(new Error("The engine has stopped"));
      this.listeners.add(fn);
      this.waiters.add(fail);
    });
  }

  private async handshake() {
    const ok = this.waitFor((l) => l === "uciok", 60000);
    this.send("uci");
    await ok;
    await this.isReady();
  }

  private async isReady() {
    const ok = this.waitFor((l) => l === "readyok");
    this.send("isready");
    await ok;
  }

  private enqueue<T>(job: () => Promise<T>): Promise<T> {
    const p = this.chain.then(job, job);
    this.chain = p.catch(() => undefined);
    return p;
  }

  analyse(fen: string, opts: AnalyseOptions = {}): Promise<AnalyseResult> {
    return this.enqueue(async () => {
      await this.ready;
      if (opts.signal?.aborted) return { bestmove: null, lines: [], depth: 0 };
      const stm = (fen.split(" ")[1] ?? "w") as "w" | "b";
      const multipv = Math.max(1, Math.min(8, opts.multipv ?? 1));
      this.send(`setoption name MultiPV value ${multipv}`);
      this.send(`setoption name Skill Level value ${opts.skill ?? 20}`);
      if (opts.elo) {
        this.send("setoption name UCI_LimitStrength value true");
        this.send(`setoption name UCI_Elo value ${Math.max(1320, Math.min(3190, Math.round(opts.elo)))}`);
      } else {
        this.send("setoption name UCI_LimitStrength value false");
      }
      await this.isReady();
      // An abort during the isready round trip would otherwise be missed: the listener
      // below is attached after it, and abort events do not replay.
      if (opts.signal?.aborted) return { bestmove: null, lines: [], depth: 0 };

      const lines = new Map<number, EngineLine>();
      let depth = 0;
      const onInfo = (line: string) => {
        const info = parseInfo(line);
        if (!info) return;
        depth = Math.max(depth, info.depth);
        lines.set(info.multipv, {
          multipv: info.multipv,
          depth: info.depth,
          score: toWhitePov(info.score, stm),
          pv: info.pv,
        });
        opts.onUpdate?.(sorted(lines), depth);
      };
      this.listeners.add(onInfo);
      const done = this.waitFor((l) => l.startsWith("bestmove"), 10 * 60 * 1000);
      const onAbort = () => this.send("stop");
      opts.signal?.addEventListener("abort", onAbort, { once: true });

      this.send(`position fen ${fen}`);
      if (opts.infinite) this.send("go infinite");
      else if (opts.movetime) this.send(`go movetime ${opts.movetime}${opts.depth ? ` depth ${opts.depth}` : ""}`);
      else this.send(`go depth ${opts.depth ?? 14}`);

      try {
        const bm = parseBestMove(await done);
        return { bestmove: bm?.best ?? null, lines: sorted(lines), depth };
      } finally {
        this.listeners.delete(onInfo);
        opts.signal?.removeEventListener("abort", onAbort);
      }
    });
  }

  terminate(reason = new Error("The engine has stopped")) {
    if (this.dead) return;
    this.dead = true;
    this.worker.terminate();
    this.listeners.clear();
    for (const fail of [...this.waiters]) fail(reason);
  }
}

function sorted(m: Map<number, EngineLine>): EngineLine[] {
  return [...m.values()].sort((a, b) => a.multipv - b.multipv);
}

let shared: StockfishEngine | null = null;

/** Shared engine for the page. Created on first use, in the browser only. */
export function getEngine(): StockfishEngine {
  if (typeof window === "undefined") throw new Error("Stockfish runs in the browser only");
  if (!shared?.alive) shared = new StockfishEngine();
  return shared;
}
